import { useState, useEffect } from 'react'
import { supabase } from './supabase'
import {
  Restaurant,
  RestaurantSettings,
  Zone,
  MarketplaceCategory,
  MenuCategory,
  MenuItem,
  MenuItemOption,
  Order,
  CustomerTrackingEvent,
} from './types'

export function useRestaurantBySlug(slug: string | undefined) {
  const [restaurant, setRestaurant] = useState<Restaurant | null>(null)
  const [settings, setSettings] = useState<RestaurantSettings | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<Error | null>(null)

  // Initial fetch
  useEffect(() => {
    if (!slug) return

    async function fetchRestaurant() {
      try {
        setLoading(true)
        const { data: restData, error: restError } = await supabase
          .from('restaurants')
          .select('*')
          .eq('slug', slug)
          .single()

        if (restError) throw restError
        setRestaurant(restData)

        if (restData) {
          const { data: setData, error: setError } = await supabase
            .from('restaurant_settings')
            .select('*')
            .eq('restaurant_id', restData.id)
            .single()

          if (!setError) setSettings(setData)
        }
      } catch (err: any) {
        console.error('Error fetching restaurant:', err)
        setError(err)
      } finally {
        setLoading(false)
      }
    }

    fetchRestaurant()
  }, [slug])

  return { restaurant, settings, loading, error }
}

export function useMarketplaceData() {
  const [zones, setZones] = useState<Zone[]>([])
  const [categories, setCategories] = useState<MarketplaceCategory[]>([])
  const [featuredRestaurants, setFeaturedRestaurants] = useState<Restaurant[]>(
    [],
  )
  const [allRestaurants, setAllRestaurants] = useState<Restaurant[]>([])
  const [restaurantCategoryMap, setRestaurantCategoryMap] = useState<
    Record<string, string[]>
  >({})
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    async function fetchData() {
      try {
        setLoading(true)
        const [zonesRes, catRes, featuredRes, allRes, catMapRes] =
          await Promise.all([
            supabase.from('zones').select('*').eq('is_active', true),
            supabase
              .from('marketplace_categories')
              .select('*')
              .eq('is_active', true)
              .order('sort_order'),
            supabase
              .from('restaurants')
              .select('*')
              .eq('is_active', true)
              .eq('is_featured', true)
              .eq('show_in_marketplace', true)
              .limit(6),
            supabase
              .from('restaurants')
              .select('*')
              .eq('is_active', true)
              .eq('show_in_marketplace', true)
              .order('name'),
            supabase
              .from('restaurant_marketplace_categories')
              .select('restaurant_id, marketplace_category_id'),
          ])

        if (zonesRes.error)
          console.error('[NOIZA] zones query error:', zonesRes.error)
        if (catRes.error)
          console.error('[NOIZA] categories query error:', catRes.error)
        if (featuredRes.error)
          console.error(
            '[NOIZA] featured restaurants query error:',
            featuredRes.error,
          )
        if (allRes.error)
          console.error('[NOIZA] all restaurants query error:', allRes.error)

        // Build a map: restaurantId -> [categoryId, categoryId, ...]
        const catMap: Record<string, string[]> = {}
        if (catMapRes.data) {
          for (const row of catMapRes.data) {
            if (!catMap[row.restaurant_id]) catMap[row.restaurant_id] = []
            catMap[row.restaurant_id].push(row.marketplace_category_id)
          }
        }

        setZones(zonesRes.data ?? [])
        setCategories(catRes.data ?? [])
        setFeaturedRestaurants(featuredRes.data ?? [])
        setAllRestaurants(allRes.data ?? [])
        setRestaurantCategoryMap(catMap)
      } catch (err) {
        console.error('[NOIZA] Error fetching marketplace data:', err)
      } finally {
        setLoading(false)
      }
    }
    fetchData()
  }, [])

  return {
    zones,
    categories,
    featuredRestaurants,
    allRestaurants,
    restaurantCategoryMap,
    loading,
  }
}

export function useRestaurants(filters?: {
  zoneId?: string
  categoryId?: string
  search?: string
}) {
  const [restaurants, setRestaurants] = useState<Restaurant[]>([])
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    async function fetchRestaurants() {
      try {
        setLoading(true)

        console.log('[NOIZA] Filter inputs:', {
          selectedZone: filters?.zoneId || '(all)',
          selectedCategory: filters?.categoryId || '(all)',
          search: filters?.search || '(none)',
        })

        // If categoryId is set and not "all", first fetch matching restaurant_ids from junction table
        let categoryRestaurantIds: string[] | null = null
        if (filters?.categoryId && filters.categoryId !== 'all') {
          const { data: catData, error: catError } = await supabase
            .from('restaurant_marketplace_categories')
            .select('restaurant_id')
            .eq('marketplace_category_id', filters.categoryId)

          if (catError) throw catError
          categoryRestaurantIds = (catData || []).map((r) => r.restaurant_id)

          console.log('[NOIZA] Category restaurant IDs:', categoryRestaurantIds)

          // If no restaurants match this category, return empty immediately
          if (categoryRestaurantIds.length === 0) {
            console.log(
              '[NOIZA] No restaurants match category, returning empty',
            )
            setRestaurants([])
            return
          }
        }

        let query = supabase
          .from('restaurants')
          .select('*')
          .eq('is_active', true)
          .eq('show_in_marketplace', true)

        // Filter by zone_slug (not zone_id)
        if (filters?.zoneId) {
          query = query.eq('zone_slug', filters.zoneId)
          console.log('[NOIZA] Filtering by zone_slug:', filters.zoneId)
        }
        if (filters?.search) query = query.ilike('name', `%${filters.search}%`)
        if (categoryRestaurantIds) query = query.in('id', categoryRestaurantIds)

        const { data, error } = await query
        if (error) throw error

        console.log('[NOIZA] Supabase returned restaurants:', data?.length ?? 0)

        setRestaurants(data || [])
      } catch (err) {
        console.error('[NOIZA] Error fetching restaurants:', err)
      } finally {
        setLoading(false)
      }
    }
    fetchRestaurants()
  }, [filters?.zoneId, filters?.categoryId, filters?.search])

  return { restaurants, loading }
}

export function useMenuData(restaurantId: string | undefined) {
  const [categories, setCategories] = useState<MenuCategory[]>([])
  const [items, setItems] = useState<MenuItem[]>([])
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    if (!restaurantId) return

    const isCaprichoId = String(restaurantId) === '2'

    async function fetchMenu() {
      try {
        setLoading(true)

        if (isCaprichoId) {
          // Capricho de Canela: read from the active view
          const { data, error } = await supabase
            .from('v_menu_items_active')
            .select('*')
            .eq('restaurant_id', restaurantId)
            .order('sort_order')

          if (error) {
            console.error('[NOIZA] v_menu_items_active error:', error)
          }

          // Product image overrides for Capricho de Canela
          const caprichoImages: Record<string, string> = {
            'Arma tu Capricho':
              'https://cdn.magicpatterns.com/uploads/29J3tJ4BxDahswLdnHaXMQ/Arma_tu_Capricho.png',
            'Capricho Caramelo':
              'https://cdn.magicpatterns.com/uploads/eutnQTQ4C3Sc6gBkoboidQ/Capricho_Caramelo.png',
            'Capricho Chocolate':
              'https://cdn.magicpatterns.com/uploads/gRbMiKdZiKP3rtx2w9C6MC/Capricho_Chocolate.png',
            'Capricho Crunch':
              'https://cdn.magicpatterns.com/uploads/cazT2xXr1u6htMgAcm7Ztg/Capricho_Crunch.png',
            'Capricho Fiesta':
              'https://cdn.magicpatterns.com/uploads/kUV3cY6Hy5pLWn39dpnmAr/Capricho_Fiesta.png',
            'Capricho Oreo':
              'https://cdn.magicpatterns.com/uploads/ujroAjP7oRGd1NTg17E5hr/Capricho_Oreo.png',
            'El Capricho Clásico':
              'https://cdn.magicpatterns.com/uploads/q5DcQFXxect8L6ddB3gpwb/El_Capricho_Clasico.png',
          }

          const viewItems: MenuItem[] = (data ?? []).map((row: any) => ({
            id: row.id,
            restaurant_id: row.restaurant_id,
            category_id: row.category_id ?? '',
            name: row.name ?? '',
            description: row.description ?? null,
            price: row.price ?? 0,
            image_url: caprichoImages[row.name] ?? row.image_url ?? null,
            item_type: row.item_type ?? null,
            subcategory: row.category_name ?? row.subcategory ?? null,
            is_customizable: row.is_customizable ?? false,
            station_code: row.station_code ?? null,
            sku: row.sku ?? null,
            is_active: true,
            sort_order: row.sort_order ?? 0,
            slug: row.slug ?? null,
            cost: row.cost ?? null,
            prep_time_minutes: row.prep_time_minutes ?? null,
            featured: row.featured ?? false,
            tags: row.tags ?? null,
            created_at: row.created_at ?? '',
            updated_at: row.updated_at ?? '',
          }))

          setItems(viewItems)
          // Categories will be auto-generated from subcategory in PublicMenuPage
          setCategories([])
        } else {
          // All other restaurants: standard tables
          const [catRes, itemsRes] = await Promise.all([
            supabase
              .from('menu_categories')
              .select('*')
              .eq('restaurant_id', restaurantId)
              .eq('is_active', true)
              .order('sort_order'),
            supabase
              .from('menu_items')
              .select('*')
              .eq('restaurant_id', restaurantId)
              .eq('is_active', true)
              .order('sort_order'),
          ])

          if (catRes.data) setCategories(catRes.data)
          if (itemsRes.data) setItems(itemsRes.data)
        }
      } catch (err) {
        console.error('Error fetching menu:', err)
      } finally {
        setLoading(false)
      }
    }
    fetchMenu()
  }, [restaurantId])

  return { categories, items, loading }
}

// Hardcoded Capricho de Canela toppings fallback
const CAPRICHO_TOPPINGS = [
  { id: 'cap-top-mani', name: 'Maní', sort_order: 1 },
  { id: 'cap-top-pecanas', name: 'Pecanas', sort_order: 2 },
  { id: 'cap-top-oreo', name: 'Galleta Oreo', sort_order: 3 },
  { id: 'cap-top-caramelo', name: 'Caramelo', sort_order: 4 },
  { id: 'cap-top-chocolate', name: 'Chocolate', sort_order: 5 },
  { id: 'cap-top-chispas', name: 'Chispas de colores', sort_order: 6 },
]
// Map product name → topping IDs to exclude (product already includes that topping)
const CAPRICHO_EXCLUSIONS: Record<string, string[]> = {
  'Capricho Caramelo': ['cap-top-caramelo'],
  'Capricho Oreo': ['cap-top-oreo'],
  'Capricho Fiesta': ['cap-top-chispas'],
  'Capricho Crunch': ['cap-top-mani', 'cap-top-pecanas'],
  'Capricho Chocolate': ['cap-top-chocolate'],
}

function getCaprichoFallbackModifiers(
  itemName: string,
  restaurantId: string,
): import('./types').ResolvedModifierGroup[] {
  const excluded = new Set(CAPRICHO_EXCLUSIONS[itemName] ?? [])
  const filteredToppings = CAPRICHO_TOPPINGS.filter((t) => !excluded.has(t.id))
  if (filteredToppings.length === 0) return []
  return [
    {
      group: {
        id: 'cap-toppings-group',
        restaurant_id: restaurantId,
        name: 'Toppings extra',
        description: 'Agregá toppings a tu rollo',
        min_select: 0,
        max_select: filteredToppings.length,
        is_required: false,
        sort_order: 1,
        is_active: true,
        created_at: '',
      },
      options: filteredToppings.map((t) => ({
        id: t.id,
        modifier_group_id: 'cap-toppings-group',
        restaurant_id: restaurantId,
        name: t.name,
        price_delta: 200,
        sort_order: t.sort_order,
        is_active: true,
        created_at: '',
      })),
    },
  ]
}

export function useMenuItemModifiers(
  menuItemId: string | undefined,
  itemContext?: { name: string; restaurant_id: string },
) {
  const [modifierGroups, setModifierGroups] = useState<
    import('./types').ResolvedModifierGroup[]
  >([])
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    if (!menuItemId) {
      setModifierGroups([])
      setLoading(false)
      return
    }

    async function fetchModifiers() {
      try {
        setLoading(true)

        // Capricho de Canela: always use hardcoded toppings (skip Supabase)
        if (itemContext && String(itemContext.restaurant_id) === '2') {
          const fallback = getCaprichoFallbackModifiers(
            itemContext.name,
            String(itemContext.restaurant_id),
          )
          setModifierGroups(fallback)
          return
        }

        // 1. Get linked modifier group IDs for this menu item
        const { data: links, error: linksError } = await supabase
          .from('menu_item_modifier_groups')
          .select('modifier_group_id')
          .eq('menu_item_id', menuItemId)

        if (linksError) throw linksError
        if (!links || links.length === 0) {
          setModifierGroups([])
          return
        }

        const groupIds = links.map((l) => l.modifier_group_id)

        // 2. Fetch the modifier groups
        const { data: groups, error: groupsError } = await supabase
          .from('modifier_groups')
          .select('*')
          .in('id', groupIds)
          .eq('is_active', true)
          .order('sort_order')

        if (groupsError) throw groupsError
        if (!groups || groups.length === 0) {
          setModifierGroups([])
          return
        }

        // 3. Fetch all options for these groups
        const { data: options, error: optionsError } = await supabase
          .from('modifier_options')
          .select('*')
          .in('modifier_group_id', groupIds)
          .eq('is_active', true)
          .order('sort_order')

        if (optionsError) throw optionsError

        // 4. Fetch exclusions for this menu item
        const { data: exclusions, error: exclError } = await supabase
          .from('menu_item_modifier_option_exclusions')
          .select('modifier_option_id')
          .eq('menu_item_id', menuItemId)

        if (exclError) throw exclError

        const excludedOptionIds = new Set(
          (exclusions || []).map((e) => e.modifier_option_id),
        )

        // 5. Build resolved groups with filtered options
        // Maintain the sort order from the link table
        const linkOrder = new Map(links.map((l, i) => [l.modifier_group_id, i]))
        const sortedGroups = [...groups].sort(
          (a, b) => (linkOrder.get(a.id) ?? 99) - (linkOrder.get(b.id) ?? 99),
        )

        const resolved = sortedGroups
          .map((group) => ({
            group,
            options: (options || []).filter(
              (opt) =>
                opt.modifier_group_id === group.id &&
                !excludedOptionIds.has(opt.id),
            ),
          }))
          .filter((r) => r.options.length > 0) // Hide groups with no visible options

        setModifierGroups(resolved)
      } catch (err) {
        console.error('Error fetching modifiers:', err)
        setModifierGroups([])
      } finally {
        setLoading(false)
      }
    }

    fetchModifiers()
  }, [menuItemId, itemContext?.name, itemContext?.restaurant_id])

  return { modifierGroups, loading }
}

export function useMenuItemOptions(menuItemId: string | undefined) {
  const [options, setOptions] = useState<MenuItemOption[]>([])
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    if (!menuItemId) return

    async function fetchOptions() {
      try {
        setLoading(true)
        const { data, error } = await supabase
          .from('menu_item_options')
          .select('*')
          .eq('menu_item_id', menuItemId)
          .eq('is_active', true)
          .order('sort_order')

        if (error) throw error
        setOptions(data || [])
      } catch (err) {
        console.error('Error fetching options:', err)
      } finally {
        setLoading(false)
      }
    }
    fetchOptions()
  }, [menuItemId])

  return { options, loading }
}

export function useOrderTracking(trackingToken: string | undefined) {
  const [order, setOrder] = useState<Order | null>(null)
  const [events, setEvents] = useState<CustomerTrackingEvent[]>([])
  const [deliveryJob, setDeliveryJob] = useState<any>(null)
  const [driverInfo, setDriverInfo] = useState<any>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<Error | null>(null)

  useEffect(() => {
    if (!trackingToken) return

    let orderId: string | null = null

    async function fetchOrder() {
      try {
        setLoading(true)
        const { data: orderData, error: orderError } = await supabase
          .from('orders')
          .select('*')
          .eq('tracking_token', trackingToken)
          .single()

        if (orderError) throw orderError
        setOrder(orderData)
        orderId = orderData?.id || null

        if (orderData) {
          const { data: eventsData, error: eventsError } = await supabase
            .from('customer_tracking_events')
            .select('*')
            .eq('order_id', orderData.id)
            .eq('visible_to_customer', true)
            .order('created_at', { ascending: false })

          if (!eventsError && eventsData) setEvents(eventsData)

          // Fetch delivery job and driver info if applicable
          if (orderData.delivery_type === 'delivery') {
            const { data: jobData } = await supabase
              .from('delivery_jobs')
              .select('id, status, driver_id, updated_at, created_at')
              .eq('order_id', orderData.id)
              .maybeSingle()

            if (jobData) {
              setDeliveryJob(jobData)
              if (jobData.driver_id) {
                const { data: driverData } = await supabase
                  .from('drivers')
                  .select('full_name')
                  .eq('id', jobData.driver_id)
                  .single()

                const { data: vehicleData } = await supabase
                  .from('vehicles')
                  .select('type, plate_number')
                  .eq('driver_id', jobData.driver_id)
                  .single()

                setDriverInfo({
                  name: driverData?.full_name || 'Conductor',
                  vehicleType: vehicleData?.type || 'Vehículo',
                  plate: vehicleData?.plate_number || 'N/A',
                })
              }
            }
          }
        }
      } catch (err: any) {
        console.error('Error fetching order:', err)
        setError(err)
      } finally {
        setLoading(false)
      }
    }

    fetchOrder()

    // Polling fallback (30s)
    const interval = setInterval(fetchOrder, 30000)

    // Realtime: listen for order status changes and new tracking events
    const orderChannel = supabase
      .channel(`tracking_${trackingToken}`)
      .on(
        'postgres_changes',
        {
          event: 'UPDATE',
          schema: 'public',
          table: 'orders',
          filter: `tracking_token=eq.${trackingToken}`,
        },
        (payload) => {
          console.log('[TRACKING] Order updated via realtime:', payload.new)
          setOrder(payload.new as Order)
          // Trigger a full fetch to get updated delivery info if status changed
          fetchOrder()
        },
      )
      .on(
        'postgres_changes',
        {
          event: 'INSERT',
          schema: 'public',
          table: 'customer_tracking_events',
        },
        (payload) => {
          const newEvent = payload.new as CustomerTrackingEvent
          // Only add if it belongs to our order
          if (
            orderId &&
            newEvent.order_id === orderId &&
            newEvent.visible_to_customer
          ) {
            console.log('[TRACKING] New tracking event via realtime:', newEvent)
            setEvents((prev) => [newEvent, ...prev])
          }
        },
      )
      .subscribe()

    return () => {
      clearInterval(interval)
      supabase.removeChannel(orderChannel)
    }
  }, [trackingToken])

  return { order, events, deliveryJob, driverInfo, loading, error }
}
